import { env } from '../../lib/env';
import { getSupabaseClient } from '../../lib/supabase';
import type { InkDocumentV1, InkPointerType } from '../../types/ink';
import type { AssignmentQrIssueResultV1, PrintableAssignmentManifestV1, PrivateAssignmentQrResolutionV1, PublicResourceResolutionV1 } from '../qr/types';
import type { TrustedAttemptSubmissionV1, TrustedGradeResultV1 } from './trustedRuntime';
import type {
  CloudAssignmentManifestV1,
  CloudLearningSessionPresenceV1,
  InkRegistrationResultV1,
  PilotFixtureProvisionResultV1,
  RuntimeRealtimeEventV1,
  RuntimeBackendStatus,
  RuntimeIdentityV1,
  RuntimeVerificationCheck,
  RuntimeVerificationReport,
  StudentNoteRecord,
} from './types';

export async function getSupabaseRuntimeStatus(): Promise<RuntimeBackendStatus> {
  const client = getSupabaseClient();
  if (!client || !env.supabaseUrl || !env.supabasePublishableKey) {
    return {
      mode: 'local_pilot',
      configured: false,
      authenticated: false,
      realtimeAvailable: false,
      label: 'Local Pilot',
      detail: 'Data masih tersimpan pada browser. Supabase belum dikonfigurasi.',
    };
  }

  const { data, error } = await client.auth.getSession();
  const authenticated = Boolean(!error && data.session?.user);
  return {
    mode: 'supabase',
    configured: true,
    authenticated,
    realtimeAvailable: authenticated,
    label: authenticated ? 'Supabase siap diuji' : 'Supabase belum login',
    detail: authenticated
      ? 'Koneksi dan sesi tersedia. Jalankan verifikasi DEV sebelum mengaktifkan sync authoritative.'
      : 'URL/key tersedia, tetapi belum ada sesi pengguna yang terautentikasi.',
  };
}

export async function getSupabaseRuntimeIdentity(): Promise<RuntimeIdentityV1 | null> {
  const client = getSupabaseClient();
  if (!client) return null;
  const { data, error } = await client.rpc('current_runtime_identity_v1');
  if (error) throw error;
  return (data ?? null) as RuntimeIdentityV1 | null;
}

export async function verifySupabaseDevRuntime(): Promise<RuntimeVerificationReport> {
  const checks: RuntimeVerificationCheck[] = [];
  const client = getSupabaseClient();

  if (!client || !env.supabaseUrl || !env.supabasePublishableKey) {
    checks.push({ key: 'configuration', label: 'Konfigurasi Supabase', status: 'blocked', detail: 'VITE_SUPABASE_URL / publishable key belum tersedia.' });
    for (const [key, label] of [
      ['session', 'Sesi autentikasi'],
      ['identity_rpc', 'Identity mapping'],
      ['curriculum_read', 'Baca curriculum'],
      ['realtime', 'Realtime channel'],
    ] as const) checks.push({ key, label, status: 'not_run', detail: 'Menunggu konfigurasi Supabase DEV.' });
    return { checkedAt: new Date().toISOString(), readyForLivePilot: false, checks };
  }

  checks.push({ key: 'configuration', label: 'Konfigurasi Supabase', status: 'pass', detail: 'URL dan publishable key tersedia.' });

  const session = await client.auth.getSession();
  if (session.error || !session.data.session?.user) {
    checks.push({ key: 'session', label: 'Sesi autentikasi', status: 'blocked', detail: session.error?.message ?? 'Belum ada user login.' });
    checks.push({ key: 'identity_rpc', label: 'Identity mapping', status: 'not_run', detail: 'Membutuhkan sesi authenticated.' });
    checks.push({ key: 'curriculum_read', label: 'Baca curriculum', status: 'not_run', detail: 'Membutuhkan sesi authenticated.' });
    checks.push({ key: 'realtime', label: 'Realtime channel', status: 'not_run', detail: 'Membutuhkan sesi authenticated.' });
    return { checkedAt: new Date().toISOString(), readyForLivePilot: false, checks };
  }
  checks.push({ key: 'session', label: 'Sesi autentikasi', status: 'pass', detail: `Authenticated sebagai ${session.data.session.user.id}.` });

  try {
    const identity = await getSupabaseRuntimeIdentity();
    checks.push({
      key: 'identity_rpc',
      label: 'Identity mapping',
      status: identity?.profileStatus === 'active' ? 'pass' : 'blocked',
      detail: identity?.student ? `Student mapping aktif: ${identity.student.studentCode}.` : 'Profile terbaca tetapi belum terhubung ke student pilot.',
    });
  } catch (error) {
    checks.push({ key: 'identity_rpc', label: 'Identity mapping', status: 'fail', detail: error instanceof Error ? error.message : 'Identity RPC gagal.' });
  }

  const curriculum = await client.from('subjects').select('code,name').eq('status', 'active').limit(6);
  checks.push({
    key: 'curriculum_read',
    label: 'Baca curriculum',
    status: curriculum.error ? 'fail' : (curriculum.data?.some((row) => row.code === 'MTH') ? 'pass' : 'blocked'),
    detail: curriculum.error?.message ?? `${curriculum.data?.length ?? 0} subject aktif terbaca.`,
  });

  // Subscription creation itself verifies the client can establish a Realtime channel.
  // We do not require an event to fire because no artificial student event should be created merely for health checking.
  let realtimeStatus: RuntimeVerificationCheck['status'] = 'pass';
  let realtimeDetail = 'Realtime channel dapat dibuat; event delivery diverifikasi saat multi-device pilot.';
  try {
    const channel = client.channel(`dev-health-${crypto.randomUUID()}`);
    channel.subscribe();
    await client.removeChannel(channel);
  } catch (error) {
    realtimeStatus = 'fail';
    realtimeDetail = error instanceof Error ? error.message : 'Realtime channel gagal dibuat.';
  }
  checks.push({ key: 'realtime', label: 'Realtime channel', status: realtimeStatus, detail: realtimeDetail });

  const readyForLivePilot = checks.every((check) => check.status === 'pass');
  return { checkedAt: new Date().toISOString(), readyForLivePilot, checks };
}


export async function fetchSupabaseAssignmentManifest(assignmentId: string): Promise<CloudAssignmentManifestV1> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');
  const { data, error } = await client.rpc('get_assignment_manifest_v1', { p_assignment_id: assignmentId });
  if (error) throw error;
  return data as CloudAssignmentManifestV1;
}


export async function checkSupabasePracticeMathAnswer(input: {
  assignmentId: string;
  questionInstanceId: string;
  value: number;
}): Promise<{ schemaVersion: 1; correct: boolean; graderVersion: string; feedbackCode: string }> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');
  const { data, error } = await client.rpc('check_pilot_math_answer_v1', {
    p_assignment_id: input.assignmentId,
    p_question_instance_id: input.questionInstanceId,
    p_response: { type: 'integer', value: input.value },
  });
  if (error) throw error;
  return data as { schemaVersion: 1; correct: boolean; graderVersion: string; feedbackCode: string };
}

export async function submitSupabaseTrustedAttempt(payload: TrustedAttemptSubmissionV1): Promise<TrustedGradeResultV1> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');

  const { data, error } = await client.rpc('submit_pilot_math_answer_v1', {
    p_client_submission_id: payload.clientSubmissionId,
    p_assignment_id: payload.assignmentRef,
    p_question_instance_id: payload.questionRef,
    p_response: payload.response,
    p_first_response: payload.firstResponse ?? payload.response,
    p_correction_count: payload.correctionCount,
    p_hint_count: payload.hintCount,
    p_elapsed_duration_ms: payload.timing.elapsedDurationMs,
    p_active_duration_ms: payload.timing.activeDurationMs,
    p_started_at: payload.startedAt,
    p_completed_at: payload.completedAt,
    p_ink_meta: payload.ink ?? null,
  });

  if (error) throw error;
  return data as TrustedGradeResultV1;
}

export async function fetchSupabaseStudentNotes(studentId: string): Promise<StudentNoteRecord[]> {
  const client = getSupabaseClient();
  if (!client) return [];

  const { data, error } = await client
    .from('student_notes')
    .select('id,student_id,author_profile_id,subject_id,skill_id,assignment_id,note_type,visibility,body,status,created_at,updated_at')
    .eq('student_id', studentId)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: String(row.id),
    studentId: String(row.student_id),
    authorRole: 'teacher' as const,
    authorName: 'Pengguna BelajarKu',
    noteType: row.note_type as StudentNoteRecord['noteType'],
    visibility: row.visibility as StudentNoteRecord['visibility'],
    body: String(row.body),
    status: row.status as StudentNoteRecord['status'],
    createdAt: String(row.created_at),
    updatedAt: String(row.updated_at),
    ...(row.subject_id ? { subjectCode: String(row.subject_id) } : {}),
    ...(row.skill_id ? { skillCode: String(row.skill_id) } : {}),
    ...(row.assignment_id ? { assignmentId: String(row.assignment_id) } : {}),
  }));
}

export function subscribeSupabaseStudentNotes(studentId: string, onChange: () => void) {
  const client = getSupabaseClient();
  if (!client) return () => undefined;

  const channel = client
    .channel(`student-notes:${studentId}`)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'student_notes',
      filter: `student_id=eq.${studentId}`,
    }, () => onChange())
    .subscribe();

  return () => { void client.removeChannel(channel); };
}

export function subscribeSupabaseLearnerProgress(studentId: string, onChange: () => void) {
  const client = getSupabaseClient();
  if (!client) return () => undefined;

  const channel = client
    .channel(`student-progress:${studentId}`)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'student_skill_progress',
      filter: `student_id=eq.${studentId}`,
    }, () => onChange())
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'mastery_records',
      filter: `student_id=eq.${studentId}`,
    }, () => onChange())
    .subscribe();

  return () => { void client.removeChannel(channel); };
}


export async function provisionSupabasePilotStudent(displayName = 'Murid Pilot'): Promise<PilotFixtureProvisionResultV1> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');
  const { data, error } = await client.rpc('provision_current_user_pilot_student_v1', { p_display_name: displayName });
  if (error) throw error;
  return data as PilotFixtureProvisionResultV1;
}

export async function createSupabaseTodayMathAssignment(learningDate?: string): Promise<CloudAssignmentManifestV1> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');
  const { data, error } = await client.rpc('create_today_math_assignment_v1', {
    p_learning_date: learningDate ?? null,
  });
  if (error) throw error;
  return data as CloudAssignmentManifestV1;
}

export async function touchSupabaseLearningSession(input: {
  clientSessionId: string;
  assignmentId: string;
  questionInstanceId?: string | null;
  status: CloudLearningSessionPresenceV1['status'];
  elapsedDurationMs: number;
  activeDurationMs: number;
  pausedDurationMs: number;
  deviceInstanceId?: string | null;
}): Promise<CloudLearningSessionPresenceV1> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');
  const { data, error } = await client.rpc('touch_learning_session_v1', {
    p_client_session_id: input.clientSessionId,
    p_assignment_id: input.assignmentId,
    p_question_instance_id: input.questionInstanceId ?? null,
    p_status: input.status,
    p_elapsed_duration_ms: Math.max(0, Math.round(input.elapsedDurationMs)),
    p_active_duration_ms: Math.max(0, Math.round(input.activeDurationMs)),
    p_paused_duration_ms: Math.max(0, Math.round(input.pausedDurationMs)),
    p_device_instance_id: input.deviceInstanceId ?? null,
  });
  if (error) throw error;
  return data as CloudLearningSessionPresenceV1;
}

function pointerTypeForDocument(document: InkDocumentV1): InkPointerType {
  const types = new Set(document.strokes.map((stroke) => stroke.pointerType));
  if (types.size === 0) return 'unknown';
  if (types.size === 1) return [...types][0] ?? 'unknown';
  return 'mixed';
}

function pointCountForDocument(document: InkDocumentV1) {
  return document.strokes.reduce((sum, stroke) => sum + stroke.points.length, 0);
}

function durationForDocument(document: InkDocumentV1) {
  let max = 0;
  for (const stroke of document.strokes) {
    for (const point of stroke.points) max = Math.max(max, point.t);
  }
  return Math.max(0, Math.round(max));
}

async function sha256Hex(value: string) {
  if (!globalThis.crypto?.subtle) return null;
  const bytes = new TextEncoder().encode(value);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function uploadSupabaseInkDocument(input: {
  studentId: string;
  attemptAnswerId: string;
  document: InkDocumentV1;
}): Promise<InkRegistrationResultV1> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');

  const body = JSON.stringify(input.document);
  const checksum = await sha256Hex(body);
  const assetName = checksum ? `${checksum}.json` : 'ink-v1.json';
  const storageKey = `${input.studentId}/${input.attemptAnswerId}/${assetName}`;
  const upload = await client.storage.from('belajarku-ink').upload(storageKey, new Blob([body], {
    type: 'application/vnd.belajarku.ink+json',
  }), { upsert: false, contentType: 'application/vnd.belajarku.ink+json' });
  if (upload.error && !/already exists|duplicate/i.test(upload.error.message)) throw upload.error;

  const { data, error } = await client.rpc('register_answer_ink_v1', {
    p_attempt_answer_id: input.attemptAnswerId,
    p_storage_key: storageKey,
    p_stroke_count: input.document.strokes.length,
    p_point_count: pointCountForDocument(input.document),
    p_input_type: pointerTypeForDocument(input.document),
    p_duration_ms: durationForDocument(input.document),
    p_canvas_width: Math.max(1, Math.round(input.document.width)),
    p_canvas_height: Math.max(1, Math.round(input.document.height)),
    p_checksum: checksum,
  });

  if (error) {
    // Orphan upload is harmless but should not accumulate in normal operation.
    // A later maintenance job can remove unregistered objects if registration fails.
    throw error;
  }
  return data as InkRegistrationResultV1;
}

export async function downloadSupabaseInkDocument(storageKey: string): Promise<InkDocumentV1> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');
  const { data, error } = await client.storage.from('belajarku-ink').download(storageKey);
  if (error) throw error;
  const parsed = JSON.parse(await data.text()) as InkDocumentV1;
  if (parsed.version !== 1 || !Array.isArray(parsed.strokes)) throw new Error('Format Ink tidak dikenali.');
  return parsed;
}

export function subscribeSupabaseStudentRuntime(
  studentId: string,
  onEvent: (event: RuntimeRealtimeEventV1) => void,
) {
  const client = getSupabaseClient();
  if (!client) return () => undefined;

  const channel = client.channel(`student-runtime:${studentId}`);
  const tables: RuntimeRealtimeEventV1['table'][] = [
    'learning_sessions',
    'student_skill_progress',
    'mastery_records',
    'student_notes',
  ];

  for (const table of tables) {
    channel.on('postgres_changes', {
      event: '*',
      schema: 'public',
      table,
      filter: `student_id=eq.${studentId}`,
    }, (payload) => onEvent({
      table,
      eventType: payload.eventType as RuntimeRealtimeEventV1['eventType'],
      newRecord: (payload.new ?? null) as Record<string, unknown> | null,
      oldRecord: (payload.old ?? null) as Record<string, unknown> | null,
    }));
  }

  channel.subscribe();
  return () => { void client.removeChannel(channel); };
}

export async function resolveSupabasePublicResource(shortCode: string): Promise<PublicResourceResolutionV1> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');
  const { data, error } = await client.rpc('resolve_resource_route_v1', { p_short_code: shortCode });
  if (error) throw error;
  return data as PublicResourceResolutionV1;
}

export async function issueSupabaseAssignmentQr(assignmentId: string, expiresInDays = 90): Promise<AssignmentQrIssueResultV1> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');
  const { data, error } = await client.rpc('issue_assignment_qr_v1', {
    p_assignment_id: assignmentId,
    p_expires_in_days: expiresInDays,
  });
  if (error) throw error;
  return data as AssignmentQrIssueResultV1;
}

export async function resolveSupabasePrivateAssignmentQr(token: string): Promise<PrivateAssignmentQrResolutionV1> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');
  const { data, error } = await client.rpc('resolve_assignment_qr_v1', { p_token: token });
  if (error) throw error;
  return data as PrivateAssignmentQrResolutionV1;
}

export async function fetchSupabasePrintableAssignment(assignmentId: string): Promise<PrintableAssignmentManifestV1> {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');
  const { data, error } = await client.rpc('get_printable_assignment_manifest_v1', { p_assignment_id: assignmentId });
  if (error) throw error;
  return data as PrintableAssignmentManifestV1;
}
